import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Select,
  Space,
  Table,
  Tag,
  TreeSelect,
  Typography,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { REPORT_STATUS_LABELS, REPORT_STATUS_OPTIONS } from '@/constants/report';
import {
  DELETE_DAILY_REPORT_AFTER_PR_SUCCESS_FAILURE,
  DELETE_DAILY_REPORT_FUSION_HINT,
  DELETE_DAILY_REPORT_FUSION_SUCCESS,
} from '@/constants/phase4-r-lite';
import { ReportDetailDrawer } from '@/pages/admin/reports/ReportDetailDrawer';
import { ApiError } from '@/services/api/client';
import {
  deleteDailyReportWithFusion,
  formatProjectReportDeleteFailureMessage,
} from '@/services/report-daily-fusion';
import * as departmentsApi from '@/services/api/departments';
import * as reportsApi from '@/services/api/reports';
import * as usersApi from '@/services/api/users';
import type { DepartmentTreeNode } from '@/types/department';
import type { ReportRecord, ReportStatus } from '@/types/report';
import { flattenDepartmentTree, toParentTreeSelectData } from '@/utils/department-tree';

interface FilterFormValues {
  dateRange?: [Dayjs, Dayjs];
  departmentId?: string;
  userId?: string;
  status?: ReportStatus;
}

interface AppliedFilters {
  startDate?: string;
  endDate?: string;
  departmentId?: string;
  userId?: string;
  status?: ReportStatus;
}

export function ReportManagementPage() {
  const { message, modal } = App.useApp();
  const [filterForm] = Form.useForm<FilterFormValues>();

  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [displayTotal, setDisplayTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<AppliedFilters>({});

  const [departmentTree, setDepartmentTree] = useState<DepartmentTreeNode[]>([]);
  const [submitterOptions, setSubmitterOptions] = useState<{ value: string; label: string }[]>([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReportId, setDetailReportId] = useState<string | null>(null);

  const departmentNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const dept of flattenDepartmentTree(departmentTree)) {
      map.set(dept.id, dept.name);
    }
    return map;
  }, [departmentTree]);

  const departmentFilterTree = useMemo(
    () => toParentTreeSelectData(departmentTree),
    [departmentTree],
  );

  const loadDepartments = useCallback(async () => {
    try {
      const tree = await departmentsApi.fetchDepartmentTree();
      setDepartmentTree(tree);
    } catch {
      setDepartmentTree([]);
    }
  }, []);

  const loadSubmitters = useCallback(async (departmentId?: string) => {
    try {
      const result = await usersApi.fetchUsers({
        page: 1,
        pageSize: 200,
        status: 'ACTIVE',
        departmentId,
      });
      setSubmitterOptions(
        result.items.map((user) => ({
          value: user.id,
          label: `${user.name}（${user.phone}）`,
        })),
      );
    } catch {
      setSubmitterOptions([]);
    }
  }, []);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await reportsApi.fetchReports({
        page,
        pageSize,
        userId: filters.userId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status,
      });

      let items = result.items;
      if (filters.departmentId && !filters.userId) {
        items = items.filter((item) => item.user.departmentId === filters.departmentId);
      }

      setReports(items);
      setDisplayTotal(
        filters.departmentId && !filters.userId ? items.length : result.total,
      );
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载日报列表失败';
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [filters, message, page, pageSize]);

  useEffect(() => {
    void loadDepartments();
    void loadSubmitters();
  }, [loadDepartments, loadSubmitters]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const handleDepartmentChange = (departmentId?: string) => {
    filterForm.setFieldValue('userId', undefined);
    void loadSubmitters(departmentId);
  };

  const handleSearch = (values: FilterFormValues) => {
    const next: AppliedFilters = {
      departmentId: values.departmentId,
      userId: values.userId,
      status: values.status,
    };

    if (values.dateRange?.[0]) {
      next.startDate = values.dateRange[0].format('YYYY-MM-DD');
    }
    if (values.dateRange?.[1]) {
      next.endDate = values.dateRange[1].format('YYYY-MM-DD');
    }

    setFilters(next);
    setPage(1);
  };

  const handleReset = () => {
    filterForm.resetFields();
    setFilters({});
    setPage(1);
    void loadSubmitters();
  };

  const openDetail = (record: ReportRecord) => {
    setDetailReportId(record.id);
    setDetailOpen(true);
  };

  const handleDelete = (record: ReportRecord) => {
    modal.confirm({
      title: '确认删除日报',
      content: (
        <div>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            确定删除「{record.user.name}」
            {new Date(record.reportDate).toLocaleDateString('zh-CN')} 的日报吗？
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {DELETE_DAILY_REPORT_FUSION_HINT}
          </Typography.Paragraph>
        </div>
      ),
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await deleteDailyReportWithFusion(
            record.userId,
            record.id,
            record.reportDate,
            undefined,
            {
              reportSubmittedAt: record.submittedAt,
              reportCreatedAt: record.createdAt,
            },
          );

          if (result.projectReportFailures.length > 0) {
            message.error(formatProjectReportDeleteFailureMessage(result.projectReportFailures));
            return;
          }

          if (result.reportDeleteFailed) {
            message.error(
              result.reportDeleteErrorMessage ?? DELETE_DAILY_REPORT_AFTER_PR_SUCCESS_FAILURE,
            );
            await loadReports();
            return;
          }

          if (result.fusionFallbackWarning) {
            message.warning(result.fusionFallbackWarning);
          }
          message.success(DELETE_DAILY_REPORT_FUSION_SUCCESS);
          if (detailReportId === record.id) {
            setDetailOpen(false);
            setDetailReportId(null);
          }
          await loadReports();
        } catch (error) {
          const text = error instanceof ApiError ? error.message : '删除失败';
          message.error(text);
          throw error;
        }
      },
    });
  };

  const columns: ColumnsType<ReportRecord> = [
    {
      title: '日报日期',
      dataIndex: 'reportDate',
      width: 120,
      render: (value: string) => new Date(value).toLocaleDateString('zh-CN'),
    },
    {
      title: '提交人',
      key: 'user',
      width: 140,
      render: (_, record) => record.user.name,
    },
    {
      title: '部门',
      key: 'department',
      width: 140,
      render: (_, record) => departmentNameMap.get(record.user.departmentId) ?? '—',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: ReportStatus) => (
        <Tag>{REPORT_STATUS_LABELS[status]}</Tag>
      ),
    },
    {
      title: '点评数',
      key: 'comments',
      width: 80,
      render: (_, record) => record.comments.length,
    },
    {
      title: '今日工作',
      dataIndex: 'todayWork',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
            详情
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total: displayTotal,
    showSizeChanger: true,
    showTotal: (count) =>
      filters.departmentId && !filters.userId
        ? `当前页 ${count} 条（部门筛选为页内过滤）`
        : `共 ${count} 条`,
    onChange: (nextPage, nextSize) => {
      setPage(nextPage);
      setPageSize(nextSize);
    },
  };

  const detailDepartmentName = detailReportId
    ? departmentNameMap.get(
        reports.find((item) => item.id === detailReportId)?.user.departmentId ?? '',
      )
    : undefined;

  return (
    <div>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        日报管理
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        查询、查看与删除系统内日报。部门筛选在未指定提交人时按当前页数据过滤。
      </Typography.Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Form<FilterFormValues> form={filterForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="dateRange" label="日期范围">
            <DatePicker.RangePicker allowClear />
          </Form.Item>
          <Form.Item name="departmentId" label="部门">
            <TreeSelect
              allowClear
              placeholder="全部部门"
              style={{ width: 220 }}
              treeData={departmentFilterTree}
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
              onChange={(value) => handleDepartmentChange(value)}
            />
          </Form.Item>
          <Form.Item name="userId" label="提交人">
            <Select
              allowClear
              showSearch
              placeholder="全部提交人"
              style={{ width: 200 }}
              optionFilterProp="label"
              options={submitterOptions}
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              allowClear
              placeholder="全部状态"
              style={{ width: 140 }}
              options={REPORT_STATUS_OPTIONS}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Table<ReportRecord>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={reports}
          pagination={pagination}
          scroll={{ x: 1000 }}
        />
      </Card>

      <ReportDetailDrawer
        open={detailOpen}
        reportId={detailReportId}
        departmentName={detailDepartmentName}
        onClose={() => {
          setDetailOpen(false);
          setDetailReportId(null);
        }}
      />
    </div>
  );
}
